import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { Icon } from '../icons';
import { Btn, ErrorBox, Loading, fmtDate } from '../ui';

type Level = 'ok' | 'warn' | 'fail';
interface Check { level: Level; name: string; detail: string }
export interface SystemStatus {
  checkedAt: string;
  overall: Level;
  checks: { api: Check[]; database: Check[]; storage: Check[]; queue: Check[]; bot: Check[] };
  host: { updatedAt: string; commit: string; subject: string; result: string; selfcheck: Check[]; readiness: Check[]; timers?: Record<string, string> } | null;
}

const LEVEL_RU: Record<Level, string> = { ok: 'в порядке', warn: 'есть замечания', fail: 'ошибка' };
const SECTIONS: { key: keyof SystemStatus['checks']; title: string; icon: string }[] = [
  { key: 'api', title: 'API', icon: 'server' },
  { key: 'database', title: 'База данных', icon: 'database' },
  { key: 'storage', title: 'Фото и диск', icon: 'disk' },
  { key: 'queue', title: 'Уведомления (воркер)', icon: 'inbox' },
  { key: 'bot', title: 'Telegram-бот', icon: 'bot' },
];

const worst = (cs: Check[]): Level => (cs.some((c) => c.level === 'fail') ? 'fail' : cs.some((c) => c.level === 'warn') ? 'warn' : 'ok');

function Checks({ items }: { items: Check[] }) {
  return (
    <ul className="checks">
      {items.map((c, i) => (
        <li key={`${c.name}-${i}`}>
          <span className={`dot lvl-${c.level}`} aria-hidden="true" />
          <span>
            {c.name}
            <span className="lvl"> — {LEVEL_RU[c.level]}</span>
          </span>
          <span>{c.detail}</span>
        </li>
      ))}
    </ul>
  );
}

export function SystemPage() {
  const { data, isLoading, error, refetch } = useQuery({ queryKey: ['system-status'], queryFn: () => api<SystemStatus>('/admin/system/status'), refetchInterval: 60_000 });
  return (
    <div>
      <div className="page-head">
        <div>
          <h1>Система</h1>
          <p className="muted">Состояние сервера и бота. Обновляется раз в минуту; о сбоях также приходит сообщение в Telegram.</p>
        </div>
        <Btn kind="secondary" onClick={() => void refetch()}>
          <Icon name="refresh" size={16} /> Проверить сейчас
        </Btn>
      </div>
      {isLoading ? <Loading /> : error || !data ? <ErrorBox error={error} onRetry={() => void refetch()} /> : (
        <>
          <div className={`status-banner lvl-${data.overall}`} role="status">
            <span className={`dot lvl-${data.overall}`} aria-hidden="true" />
            <div>
              <b>{data.overall === 'ok' ? 'Всё работает' : data.overall === 'warn' ? 'Работает, есть замечания' : 'Есть проблемы — нужна проверка'}</b>
              <div className="muted small">Проверено {fmtDate(data.checkedAt)}</div>
            </div>
          </div>

          <div className="panels">
            {SECTIONS.map((s) => {
              const items = data.checks[s.key] ?? [];
              if (!items.length) return null;
              const lvl = worst(items);
              return (
                <section key={s.key} className="panel" aria-label={s.title}>
                  <div className="chart-head">
                    <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Icon name={s.icon} /> {s.title}
                    </h2>
                    <span className={`pill ${lvl === 'ok' ? 'pill-ok' : lvl === 'warn' ? 'pill-warn' : 'pill-danger'}`}>{LEVEL_RU[lvl]}</span>
                  </div>
                  <Checks items={items} />
                </section>
              );
            })}
          </div>

          <div className="panels">
            <section className="panel" aria-label="Последнее обновление сервера">
              <h2>Последнее обновление сервера</h2>
              {data.host ? (
                <>
                  <p className="sub">
                    {fmtDate(data.host.updatedAt)} · версия <code>{data.host.commit.slice(0, 7)}</code>
                  </p>
                  <p style={{ margin: '0 0 10px' }}>{data.host.subject}</p>
                  {data.host.timers ? (
                    <p className="muted small">
                      Автообновление: {data.host.timers['qm-autoupdate'] === 'active' ? 'включено' : data.host.timers['qm-autoupdate']} · Ежедневный бэкап:{' '}
                      {data.host.timers['qm-backup'] === 'active' ? 'включён' : data.host.timers['qm-backup']}
                    </p>
                  ) : null}
                  <h3>Самопроверка бота</h3>
                  <Checks items={data.host.selfcheck} />
                </>
              ) : (
                <p className="muted">Данные появятся после следующего обновления сервера.</p>
              )}
            </section>
            <section className="panel" aria-label="Готовность сервера">
              <h2>Готовность сервера</h2>
              <p className="sub">Настройки, контейнеры, сертификаты, фото, место на диске и бэкап — проверяются при каждом обновлении</p>
              {data.host ? <Checks items={data.host.readiness} /> : <p className="muted">Данные появятся после следующего обновления сервера.</p>}
            </section>
          </div>
        </>
      )}
    </div>
  );
}
